import { BoolValue, StringValue } from "google-protobuf/google/protobuf/wrappers_pb";
import { StatusCode } from "grpc-web";
import { LiteUser, User } from "proto/api_pb";
import {
  CreateGroupChatReq,
  EditGroupChatReq,
  GetDirectMessageReq,
  GetGroupChatMessagesReq,
  GetGroupChatReq,
  InviteToGroupChatReq,
  LeaveGroupChatReq,
  ListGroupChatsReq,
  ListMessageThreadsReq,
  MakeGroupChatAdminReq,
  MarkAllThreadsSeenReq,
  MarkLastSeenGroupChatReq,
  MessageThreadCategory,
  MuteGroupChatReq,
  RemoveGroupChatAdminReq,
  SendDirectMessageReq,
  SendMessageReq,
  SetGroupChatArchiveStatusReq,
} from "proto/conversations_pb";
import { Temporal } from "temporal-polyfill";
import { durationToProtobuf } from "utils/date";

import client from "./client";
import isGrpcError from "./utils/isGrpcError";

export async function listGroupChats(lastMessageId = 0, count = 10, onlyArchived?: boolean) {
  const req = new ListGroupChatsReq();
  req.setLastMessageId(lastMessageId);
  req.setNumber(count);
  if (onlyArchived !== undefined) {
    req.setOnlyArchived(onlyArchived);
  }

  const response = await client.conversations.listGroupChats(req);

  return response.toObject();
}

export async function listMessageThreads({
  categories,
  onlyUnread,
  onlyArchived,
  pageToken,
  pageSize,
}: {
  categories: MessageThreadCategory[];
  onlyUnread?: boolean;
  onlyArchived?: boolean;
  pageToken?: string;
  pageSize?: number;
}) {
  const req = new ListMessageThreadsReq();
  req.setCategoriesList(categories);
  if (onlyUnread !== undefined) {
    req.setOnlyUnread(onlyUnread);
  }
  if (onlyArchived !== undefined) {
    req.setOnlyArchived(onlyArchived);
  }
  if (pageToken) {
    req.setPageToken(pageToken);
  }
  if (pageSize !== undefined) {
    req.setPageSize(pageSize);
  }

  const response = await client.conversations.listMessageThreads(req);

  return response.toObject();
}

export async function markAllThreadsSeen({
  categories,
  onlyUnread,
  onlyArchived,
}: {
  categories: MessageThreadCategory[];
  onlyUnread?: boolean;
  onlyArchived?: boolean;
}) {
  const req = new MarkAllThreadsSeenReq();
  req.setCategoriesList(categories);
  if (onlyUnread !== undefined) {
    req.setOnlyUnread(onlyUnread);
  }
  if (onlyArchived !== undefined) {
    req.setOnlyArchived(onlyArchived);
  }
  return client.conversations.markAllThreadsSeen(req);
}

export async function getGroupChat(id: number) {
  const req = new GetGroupChatReq();
  req.setGroupChatId(id);
  const response = await client.conversations.getGroupChat(req);
  return response.toObject();
}

export async function getGroupChatMessages(groupChatId: number, lastMessageId = 0, count = 20) {
  const req = new GetGroupChatMessagesReq();
  req.setGroupChatId(groupChatId);
  req.setLastMessageId(lastMessageId);
  req.setNumber(count);

  const response = await client.conversations.getGroupChatMessages(req);

  return response.toObject();
}

export async function createGroupChat(title: string, users: User.AsObject[]): Promise<number> {
  const req = new CreateGroupChatReq();
  req.setRecipientUserIdsList(users.map((user) => user.userId));
  req.setTitle(new StringValue().setValue(title));
  const response = await client.conversations.createGroupChat(req);
  const groupChatId = response.getGroupChatId();

  return groupChatId;
}

export async function sendMessage(groupChatId: number, text: string) {
  const req = new SendMessageReq();
  req.setGroupChatId(groupChatId);
  req.setText(text);
  return await client.conversations.sendMessage(req);
}

export async function sendDirectMessage(recipientUserId: number, text: string): Promise<number> {
  const req = new SendDirectMessageReq();
  req.setRecipientUserId(recipientUserId);
  req.setText(text);
  const response = await client.conversations.sendDirectMessage(req);
  return response.getGroupChatId();
}

export function leaveGroupChat(groupChatId: number) {
  const req = new LeaveGroupChatReq();
  req.setGroupChatId(groupChatId);
  return client.conversations.leaveGroupChat(req);
}

export function inviteToGroupChat(groupChatId: number, users: User.AsObject[]) {
  const promises = users.map((user) => {
    const req = new InviteToGroupChatReq();
    req.setGroupChatId(groupChatId);
    req.setUserId(user.userId);
    return client.conversations.inviteToGroupChat(req);
  });
  return Promise.all(promises);
}

export function makeGroupChatAdmin(groupChatId: number, user: LiteUser.AsObject) {
  const req = new MakeGroupChatAdminReq();
  req.setGroupChatId(groupChatId);
  req.setUserId(user.userId);
  return client.conversations.makeGroupChatAdmin(req);
}

export function removeGroupChatAdmin(groupChatId: number, user: LiteUser.AsObject) {
  const req = new RemoveGroupChatAdminReq();
  req.setGroupChatId(groupChatId);
  req.setUserId(user.userId);
  return client.conversations.removeGroupChatAdmin(req);
}

export function editGroupChat(groupChatId: number, title?: string, onlyAdminsInvite?: boolean) {
  const req = new EditGroupChatReq();
  req.setGroupChatId(groupChatId);
  if (title !== undefined) req.setTitle(new StringValue().setValue(title));
  if (onlyAdminsInvite !== undefined) req.setOnlyAdminsInvite(new BoolValue().setValue(onlyAdminsInvite));
  return client.conversations.editGroupChat(req);
}

export function markLastSeenGroupChat(groupChatId: number, lastSeenMessageId: number) {
  const req = new MarkLastSeenGroupChatReq();
  req.setGroupChatId(groupChatId);
  req.setLastSeenMessageId(lastSeenMessageId);
  return client.conversations.markLastSeenGroupChat(req);
}

export async function getDirectMessage(userId: number) {
  const req = new GetDirectMessageReq();
  req.setUserId(userId);
  try {
    const res = await client.conversations.getDirectMessage(req);
    return res.getGroupChatId();
  } catch (e) {
    if (isGrpcError(e) && e.code === StatusCode.NOT_FOUND) {
      return false;
    } else {
      throw e;
    }
  }
}

export type MuteChatOptions = Pick<MuteGroupChatReq.AsObject, "groupChatId"> &
  Partial<Omit<MuteGroupChatReq.AsObject, "groupChatId" | "forDuration">> & {
    forDuration?: Temporal.Duration;
  };
export async function muteChat(options: MuteChatOptions) {
  const req = new MuteGroupChatReq();
  req.setGroupChatId(options.groupChatId);
  if (options.unmute) req.setUnmute(true);
  if (options.forever) req.setForever(true);
  if (options.forDuration) req.setForDuration(durationToProtobuf(options.forDuration));
  return client.conversations.muteGroupChat(req);
}

export async function setGroupChatArchiveStatus(groupChatId: number, isArchived: boolean) {
  const req = new SetGroupChatArchiveStatusReq();
  req.setGroupChatId(groupChatId);
  req.setIsArchived(isArchived);
  return client.conversations.setGroupChatArchiveStatus(req);
}
